import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import { useTokenBadges } from '../../hooks/useTokenBadges';
import type { TokenBadge } from './tokens';

/** The round chip: ring (solid, or a conic gradient for a recovering shield), dark core, shaped icon. */
const Chip: React.FC<{ b: TokenBadge; size: number }> = ({ b, size }) => {
  const core = Math.round(size * 0.8);
  const icon = Math.round(size * 0.45);
  return (
    <span className="flex flex-none items-center justify-center rounded-full" style={{ width: size, height: size, background: b.ring }}>
      <span className="flex items-center justify-center rounded-full bg-[#0b0f18]" style={{ width: core, height: core }}>
        {b.glyph ? (
          <span className="font-ut-display font-extrabold leading-none" style={{ fontSize: Math.round(size * 0.62), color: b.colour }}>
            {b.glyph}
          </span>
        ) : (
          <span style={{ width: icon, height: icon, background: b.colour, clipPath: b.clip }} />
        )}
      </span>
    </span>
  );
};

/**
 * One badge with its detail: hover shows the title, tap opens a small popover
 * (portalled, so the card's shield clip-path can't cut it off). Tapping a
 * badge never follows the card's link.
 */
const Badge: React.FC<{ b: TokenBadge; size: number; label: boolean }> = ({ b, size, label }) => {
  const [open, setOpen] = useState<DOMRect | null>(null);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(null);
    const t = setTimeout(close, 4000);
    window.addEventListener('scroll', close, true);
    window.addEventListener('pointerdown', close);
    return () => {
      clearTimeout(t);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('pointerdown', close);
    };
  }, [open]);

  const toggle = (e: React.SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen((o) => (o ? null : ref.current?.getBoundingClientRect() ?? null));
  };

  return (
    <>
      <span
        ref={ref}
        role="button"
        tabIndex={0}
        title={b.tip}
        aria-label={b.tip}
        onClick={toggle}
        onPointerDown={(e) => e.stopPropagation()}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && toggle(e)}
        className={clsx(
          'pointer-events-auto flex flex-none cursor-help items-center rounded-full shadow-[0_1px_4px_rgba(0,0,0,.5)]',
          label && 'gap-[5px] bg-[rgba(8,10,16,.85)] py-[3px] pl-[3px] pr-[9px] font-ut-display text-xs font-extrabold tracking-[0.08em] text-[#f2efe6]',
        )}
      >
        <Chip b={b} size={size} />
        {label && b.short}
      </span>
      {open &&
        createPortal(
          <span
            role="tooltip"
            className="fixed z-[100] max-w-[240px] rounded-lg bg-[#0b0f18] px-2.5 py-2 text-left font-ut-body text-xs font-medium leading-snug text-[#f2efe6] shadow-lg"
            style={{ top: open.bottom + 6, right: Math.max(8, window.innerWidth - open.right) }}
          >
            <b className="font-ut-display text-[11px] font-extrabold tracking-[0.08em]" style={{ color: b.colour }}>
              {b.short}
            </b>
            <br />
            {b.tip}
          </span>,
          document.body,
        )}
    </>
  );
};

/**
 * A player's token badges (priority, shield, injury, unpaid), from the shared store.
 * - `stack`: a column of round chips (small cards; place it top-right).
 * - `pills`: a column of chips with labels, e.g. "SHIELD 3/5" (large cards).
 * - `inline`: a row of small chips, e.g. after a name in a list row.
 */
export const TokenChips: React.FC<{ playerId: string | null | undefined; variant?: 'stack' | 'pills' | 'inline'; size?: number; className?: string }> = ({
  playerId,
  variant = 'stack',
  size,
  className,
}) => {
  const badges = useTokenBadges(playerId);
  if (!badges.length) return null;
  const s = size ?? (variant === 'inline' ? 16 : 20);
  return (
    <span className={clsx('flex', variant === 'inline' ? 'flex-row items-center gap-1' : 'flex-col items-end gap-[3px]', className)}>
      {badges.map((b) => (
        <Badge key={b.k} b={b} size={s} label={variant === 'pills'} />
      ))}
    </span>
  );
};
